/**
 * Radio Option component module.
 * @module shared/components/RadioOption
 *
 */
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components/macro';
import {fill} from './cssUtils';
import Row from './Row';
import {Icon} from '../index';

const _Container = styled.div`
  display: inline-block;
  ${props => fill(props.width)};
  margin: 5px;
  min-width: 20px;
  height: auto;
  opacity: ${props => props.disabled ? 0.5 : 1};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};

  ${props => props.value
          && `
          & svg #Background {
            opacity: 0.1;
          }
          & svg #Selection {
            opacity: 1;
          }
          `
          || `
          & svg #Background {
            opacity: 0;
          }
          & svg #Selection {
            opacity: 0;
          }
          `}
  &:hover svg #Background {
    ${props => (!props.disabled && 'opacity: 0.15') || ''};
  }
`;

const _Option = styled.input`
  display: none;
`;

const _Label = styled.label`
  font-family: ${(props) => props.theme.labelText.name};
  color: ${(props) => props.theme.dark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.9)'};
  text-align: left;
  margin-left: 5px;
  ${() => fill(null)};
  word-wrap: break-word;
  cursor: pointer;
`;

/**
 * Radio Option component
 * @name RadioOption
 */
const RadioOption = (props) => {
  const {
    dataPoint,
    disabled,
    label,
    onChange,
    selected,
    width,
  } = props;
  const {name, value} = dataPoint;
  const opts = {disabled, name, value: selected !== undefined ? selected : value, width};

  return <_Container {...opts} onClick={(e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return '';
    return onChange(name, value);
  }}>
    <Row justifySubRow='left' wrap={false}>
      <_Option id={`radio-${name}-${label}`}
               checked={selected}
               name={dataPoint.name}
               type='radio'
               value={value}
               onChange={(e) => {
                 e.preventDefault();
                 e.stopPropagation();
                 if (disabled) return '';
                 return onChange(name, value);
               }}/>
      <Icon margin={'0'} width={'20px'} height={'20px'} disabled={disabled} svg={'radio'}/>
      {
        label && <_Label htmlFor={`radio-${name}-${label}`}>{label}</_Label>
      }
    </Row>
  </_Container>;
};

export default RadioOption;

RadioOption.propTypes = {
  /**
   * The attribute being set
   */
  dataPoint: PropTypes.shape({
    name: PropTypes.string.isRequired,
    value: PropTypes.bool,
  }).isRequired,
  /**
   * Dictates whether the raid option is selected
   */
  selected: PropTypes.bool,
  /**
   * If true, then Radio Button is disabled
   */
  disabled: PropTypes.bool,
  /**
   * Displayed Label for the Radio Option
   */
  label: PropTypes.string,
  /**
   * The on change function (name, value)
   */
  onChange: (props, propName, componentName) => {
    PropTypes.checkPropTypes({[propName]: PropTypes.func}, props, propName, componentName);
    if (!props.disabled && !props[propName]) {
      return new Error(`If ${componentName} is not disabled, then ${propName} is required.`);
    }
    return null;
  },
  /**
   * The width of the Radio Option
   */
  width: PropTypes.string,
};
